import PropTypes from 'prop-types';
import { useState } from 'react';
import { alpha } from '@mui/material/styles';
import { LoadingButton } from '@mui/lab';
import { Box, Button, Card, Paper, Stack, Typography } from '@mui/material';
import { isSupportChatOpen, openSupportChat } from '@/components/gleap-chat/GleapChat';
import ConfirmDialog from '@/components/confirm-dialog';
import Iconify from '@/components/iconify';
import { rejectionColor } from '@/consts/businesses';
import { HELP_CENTER_SUPPORT_URL } from '@/utils/external-links';

// O tema capitaliza cada palavra do botao, o que vira "Continuar Para
// Documentos". O protótipo escreve em frase, entao estas telas desligam a regra.
const BOTAO_EM_FRASE = { textTransform: 'none' };

// Abaixo de 1s porque e o quanto o Safari ainda trata a aba nova como resposta
// ao clique; passando disso ele bloqueia o window.open.
const CHAT_OPEN_CHECK_DELAY = 800;

// Suporte abre em aba nova em toda a plataforma. Com noopener o window.open
// devolve null mesmo quando a aba abre, entao o retorno nao detecta bloqueio:
// usa-lo como sinal trocava a plataforma pela central de ajuda.
const abrirCentralDeAjuda = () =>
  window.open(HELP_CENTER_SUPPORT_URL, '_blank', 'noopener,noreferrer');

const SUBTITULO_PADRONIZADO = {
  corrigivel:
    'Nossa equipe de Compliance identificou pendências no cadastro. Corrija as informações ou documentos necessários e envie para uma nova análise.',
  definitivo: 'Nossa equipe de Compliance identificou pendências no cadastro.',
};

const SUBTITULO_ANTERIOR = {
  corrigivel:
    'Nossa equipe de Compliance não aprovou o cadastro. Corrija as informações ou documentos necessários e envie para uma nova análise.',
  definitivo: 'Não foi possível validar seu cadastro no momento.',
};

const RECUSA_DEFINITIVA = {
  titulo: 'Não foi possível aprovar seu cadastro.',
  descricao: 'Após a análise, sua solicitação foi recusada e não será possível enviá-la novamente.',
};

const CampoDoMotivo = ({ label, value, color }) => (
  <Stack spacing={0.5}>
    <Typography variant="caption" sx={{ fontWeight: 'fontWeightBold', color: `${color}.dark` }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
      {value}
    </Typography>
  </Stack>
);

CampoDoMotivo.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string,
  color: PropTypes.string,
};

export default function BusinessRejectedCard({ business, onFix, onDelete, isDeleting }) {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  // Sem rejection o backend ainda nao expoe a decisao do analista (CBK-540), e o
  // card segue oferecendo a correcao como antes, em vez de travar quem podia
  // corrigir. Com rejection, quem decide e o can_fix.
  const canFix = business?.rejection?.canFix ?? true;
  // Reprovacao definitiva nao mostra motivo, mesmo que ele venha (CBK-526 5.10).
  const reason = canFix ? business?.rejection?.reason : null;
  const standardized = Boolean(business?.rejection?.standardized);
  const howToFix = canFix ? business?.rejection?.howToFix : null;
  const complement = canFix ? business?.rejection?.complement : null;
  const color = rejectionColor({ canFix });
  const subtitulos = standardized ? SUBTITULO_PADRONIZADO : SUBTITULO_ANTERIOR;

  // O chat e o canal preferido, mas ele aceita a chamada mesmo quando nao abre,
  // e ai o clique ficava sem efeito nenhum. Por isso o resultado e conferido, e
  // a central de ajuda entra como saida.
  const handleSupport = () => {
    if (!openSupportChat()) {
      abrirCentralDeAjuda();
      return;
    }

    setTimeout(() => {
      if (!isSupportChatOpen()) {
        abrirCentralDeAjuda();
      }
    }, CHAT_OPEN_CHECK_DELAY);
  };

  const handleConfirmDelete = () => {
    setIsConfirmOpen(false);
    onDelete();
  };

  return (
    <Card sx={{ p: { xs: 3, md: 4 } }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
        <Box
          sx={{
            width: 56,
            height: 56,
            flexShrink: 0,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: `${color}.main`,
            bgcolor: (theme) => alpha(theme.palette[color].main, 0.16),
          }}
        >
          <Iconify icon="solar:danger-triangle-bold" width={28} />
        </Box>

        <Stack spacing={3} sx={{ flexGrow: 1 }}>
          <Stack spacing={1}>
            <Typography variant="h5">Não foi possível aprovar o seu novo negócio</Typography>

            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {canFix ? subtitulos.corrigivel : subtitulos.definitivo}
            </Typography>
          </Stack>

          {standardized && canFix && (
            <Paper
              variant="outlined"
              sx={{
                p: 2.5,
                borderColor: (theme) => alpha(theme.palette[color].main, 0.48),
                bgcolor: (theme) => alpha(theme.palette[color].main, 0.08),
              }}
            >
              <Stack spacing={2}>
                {reason && <CampoDoMotivo label="Motivo" value={reason} color={color} />}
                {howToFix && <CampoDoMotivo label="Como resolver" value={howToFix} color={color} />}
                {complement && (
                  <CampoDoMotivo label="Informação adicional" value={complement} color={color} />
                )}
              </Stack>
            </Paper>
          )}

          {standardized && !canFix && (
            <Paper
              variant="outlined"
              sx={{
                p: 2.5,
                borderColor: (theme) => alpha(theme.palette[color].main, 0.48),
                bgcolor: (theme) => alpha(theme.palette[color].main, 0.08),
              }}
            >
              <Typography variant="subtitle2">{RECUSA_DEFINITIVA.titulo}</Typography>
              <Typography variant="body2">{RECUSA_DEFINITIVA.descricao}</Typography>
            </Paper>
          )}

          {!standardized && reason && (
            <Paper
              variant="outlined"
              sx={{
                p: 2.5,
                borderColor: (theme) => alpha(theme.palette.warning.main, 0.48),
                bgcolor: (theme) => alpha(theme.palette.warning.main, 0.08),
              }}
            >
              <Typography variant="body2">{reason}</Typography>
            </Paper>
          )}

          <Stack
            spacing={1.5}
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ sm: 'center' }}
          >
            {canFix && (
              <Button
                sx={BOTAO_EM_FRASE}
                variant="contained"
                onClick={onFix}
                endIcon={<Iconify icon="eva:arrow-forward-fill" />}
              >
                Corrigir e enviar de novo
              </Button>
            )}

            <Button variant="outlined" color="inherit" onClick={handleSupport} sx={BOTAO_EM_FRASE}>
              Falar com o Suporte
            </Button>

            <LoadingButton
              color="inherit"
              loading={isDeleting}
              onClick={() => setIsConfirmOpen(true)}
              sx={{ ...BOTAO_EM_FRASE, color: 'text.secondary' }}
            >
              Excluir este negócio
            </LoadingButton>
          </Stack>
        </Stack>
      </Stack>

      <ConfirmDialog
        open={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        title="Excluir este negócio"
        content="Os registros deste negócio saem da sua conta e ele deixa de aparecer no seletor."
        action={
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            sx={BOTAO_EM_FRASE}
          >
            Excluir negócio
          </Button>
        }
      />
    </Card>
  );
}

BusinessRejectedCard.propTypes = {
  business: PropTypes.object,
  onFix: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  isDeleting: PropTypes.bool,
};
